import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRightLeft, AlertTriangle, MapPin, Inbox, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CollectorAvatar } from "@/components/CollectorAvatar";

const BLOCKED = { already_has_lead: "Already has this request", not_receiving_leads: "Profile incomplete — can't receive leads" };

const warningsFor = (c, data) => {
  const w = [];
  if (!c.offers_service) w.push(`doesn't offer ${data.services.map((s) => s.label).join(", ")}`);
  if (!c.in_area) w.push(`is outside the requested area (${data.zip_codes.join(", ")})`);
  return w;
};

const Option = ({ c, data, checked, onPick }) => {
  const warn = warningsFor(c, data);
  return (
    <label className={`flex items-start gap-3 px-3 py-2.5 ${c.can_assign ? "cursor-pointer hover:bg-[#F0EFEA]/60" : "opacity-55"}`} data-testid={`reassign-option-${c.id}`}>
      <input type="radio" name="reassign-target" className="mt-1.5 text-[#2D6A4F]" disabled={!c.can_assign} checked={checked} onChange={() => onPick(c.id)} data-testid={`reassign-radio-${c.id}`} />
      <CollectorAvatar name={c.name} photo={c.photo_url} size="w-8 h-8" />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-medium text-[#1E3F33] truncate">{c.name}</span>
          {c.best && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#2D6A4F] text-white font-medium whitespace-nowrap inline-flex items-center gap-1" data-testid={`reassign-best-${c.id}`}><Sparkles className="w-2.5 h-2.5" /> Best match</span>}
        </span>
        <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] mt-0.5">
          {!c.can_assign ? <span className="text-[#8A9A86]">{BLOCKED[c.blocked_reason]}</span> : warn.length ? (
            <span className="text-[#8A5A00] inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {warn.map((x) => x.split(" (")[0]).join(" · ")}</span>
          ) : <span className="text-[#2D6A4F]">Offers the service · covers this area</span>}
          {typeof c.distance_miles === "number" && <span className="text-[#5C6B64] inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> ~{c.distance_miles} mi</span>}
          {c.can_assign && <span className="text-[#5C6B64] inline-flex items-center gap-1" data-testid={`reassign-open-${c.id}`}><Inbox className="w-3 h-3" /> {c.open_leads} open</span>}
        </span>
      </span>
    </label>
  );
};

export const ReassignLeadDialog = ({ lead, open, onOpenChange, onDone }) => {
  const [data, setData] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null); setPick("");
    api.get(`/company/leads/${lead.id}/reassign-options`).then(({ data }) => { setData(data); setPick(data.collectors.find((c) => c.best)?.id || ""); })
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); onOpenChange(false); });
  }, [open, lead.id, onOpenChange]);

  const target = data?.collectors.find((c) => c.id === pick);
  const warn = target ? warningsFor(target, data) : [];
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/company/leads/${lead.id}/reassign`, { collector_id: pick });
      toast.success(`Lead moved to ${target.name} — they've been alerted`);
      onOpenChange(false);
      await onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#1E3F33] flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-[#CC5A3A]" /> Reassign lead</DialogTitle>
          <DialogDescription>Move {lead.customer_name || "this customer"}'s request from <span className="font-medium text-[#1E3F33]">{data?.current.name || lead.collector_name}</span> to another collector on your team.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="text-sm text-[#5C6B64] py-4">Loading…</div> : data.collectors.length === 0 ? (
          <p className="text-sm text-[#8A9A86]" data-testid="reassign-empty">No other collectors on your team yet. Add one from the Collectors tab.</p>
        ) : (
          <div>
            <div className="max-h-72 overflow-auto rounded-xl border border-[#1E3F33]/10 divide-y divide-[#1E3F33]/8" data-testid="reassign-options">
              {data.collectors.map((c) => <Option key={c.id} c={c} data={data} checked={pick === c.id} onPick={setPick} />)}
            </div>
            {warn.length > 0 && (
              <div className="mt-3 rounded-lg bg-[#E9C46A]/20 border border-[#E9C46A] px-3 py-2 text-xs text-[#8A5A00] flex gap-2" data-testid="reassign-warning">
                <AlertTriangle className="w-4 h-4 shrink-0" /> <span>{target.name} {warn.join(" and ")}. You can still move it if you're sure they can help this customer.</span>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => onOpenChange(false)} className="btn-secondary text-sm py-2 px-4" data-testid="reassign-cancel">Cancel</button>
              <button type="button" onClick={submit} disabled={!pick || busy} className="btn-primary text-sm py-2 px-4 disabled:opacity-50" data-testid="reassign-confirm">
                {busy ? "Moving…" : warn.length ? "Move anyway" : target ? `Move to ${target.name.split(" ")[0]}` : "Move lead"}
              </button>
            </div>
            <p className="text-[11px] text-[#8A9A86] mt-3">The new collector gets the usual new-lead alert. The previous collector sees a note that it was moved.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
